import type { ColorParam } from '@paint-ai/shared';
import { useState } from 'react';
import { Card, SectionHeading } from '@/components/ui';
import { useShadeLibrary } from '../../hooks/useShadeLibrary';
import type { ColorScheme, ColorSlot } from '../../state/studio-reducer';
import { ColorSlotEditor, type ColorSlotEditorProps } from './ColorSlotEditor';
import { ShadeLibraryBanner } from './ShadeLibraryBanner';

const SLOTS: { slot: ColorSlot; label: string; role: ColorSlotEditorProps['role'] }[] = [
  { slot: 'wall', label: 'Primary Wall', role: 'Primary' },
  { slot: 'border', label: 'Trim & Border', role: 'Trim' },
  { slot: 'highlight', label: 'Feature Highlight', role: 'Accent' },
];

interface ColorConfiguratorProps {
  colors: ColorScheme;
  onChange: (slot: ColorSlot, color: ColorParam) => void;
}

/** Assigns shade name, code and HEX for the wall, trim and highlight surfaces. */
export function ColorConfigurator({ colors, onChange }: ColorConfiguratorProps) {
  const library = useShadeLibrary();
  // Only one shade dropdown is open at a time; clicking outside closes it.
  const [openSlot, setOpenSlot] = useState<ColorSlot | null>(null);

  return (
    <Card className="relative">
      {openSlot && <div className="fixed inset-0 z-20" aria-hidden onClick={() => setOpenSlot(null)} />}

      <SectionHeading
        title="Exterior Color & Shade Mapping"
        description="Search and assign exact Shade Name, Shade Code, and HEX values for Primary Wall, Trim, and Feature surfaces"
      />

      <ShadeLibraryBanner shadeCount={library.shades.length} status={library.status} onImport={library.importFile} />

      <div className="space-y-3.5">
        {SLOTS.map(({ slot, label, role }) => (
          <ColorSlotEditor
            key={slot}
            id={slot}
            label={label}
            role={role}
            color={colors[slot]}
            shades={library.shades}
            onChange={(color) => onChange(slot, color)}
            searchOpen={openSlot === slot}
            onSearchOpen={() => setOpenSlot(slot)}
            onSearchClose={() => setOpenSlot(null)}
          />
        ))}
      </div>
    </Card>
  );
}
